import { createContext, useContext, useState, ReactNode } from 'react';

type AdminMode = 'admin' | 'member';

interface AdminModeContextType {
  adminMode: AdminMode;
  setAdminMode: (mode: AdminMode) => void;
  toggleAdminMode: () => void;
}

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

export const AdminModeProvider = ({ children }: { children: ReactNode }) => {
  const [adminMode, setAdminMode] = useState<AdminMode>(() => {
    // Load from localStorage, default to 'admin'
    const saved = localStorage.getItem('adminMode');
    return (saved as AdminMode) || 'admin';
  });

  const setAdminModeWithPersistence = (mode: AdminMode) => {
    setAdminMode(mode);
    localStorage.setItem('adminMode', mode);
  };

  const toggleAdminMode = () => {
    const newMode: AdminMode = adminMode === 'admin' ? 'member' : 'admin';
    setAdminModeWithPersistence(newMode);
  };

  return (
    <AdminModeContext.Provider value={{ 
      adminMode, 
      setAdminMode: setAdminModeWithPersistence, 
      toggleAdminMode 
    }}>
      {children}
    </AdminModeContext.Provider>
  );
};

export const useAdminMode = () => {
  const context = useContext(AdminModeContext);
  if (!context) {
    throw new Error('useAdminMode must be used within AdminModeProvider');
  }
  return context;
}; 